import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { Container } from './Container'
import { MobileMenu } from './MobileMenu'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import { Button } from '../ui/Button'
import { Footer } from './Footer'
import { cn } from '../../lib/utils'

interface PlatformShellProps {
  children: ReactNode
}

export function PlatformShell({ children }: PlatformShellProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const platformLinks = [
    { label: t('header.brain'), href: '/plataforma/brain' },
    { label: t('header.build'), href: '/plataforma/build' },
    { label: t('header.govern'), href: '/plataforma/govern' },
  ]

  const pageLinks = [
    ...platformLinks,
    { label: t('header.howWeWork'), href: '/how-we-work' },
  ]

  const navItems = pageLinks

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-border/50 bg-bg/80 backdrop-blur-xl">
        <Container size="wide">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-xl font-semibold text-gradient">
              <span>EmpleaHoy IA</span>
            </Link>

            <nav className="hidden items-center gap-8 lg:flex">
              {pageLinks.map((link) => {
                const isActive = link.href.startsWith('/#')
                  ? false
                  : location.pathname === link.href
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={cn(
                      'text-sm font-medium transition-colors',
                      isActive
                        ? 'text-accent-light'
                        : 'text-text-secondary hover:text-text-primary',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                )
              })}
            </nav>

            <div className="hidden items-center gap-4 lg:flex">
              <LanguageSwitcher />
              <Link
                to="/admin"
                className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
              >
                {t('header.admin')}
              </Link>
              <Button asChild size="md">
                <a href="/#diagnostico">{t('header.cta')}</a>
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="rounded-full p-2 text-text-secondary hover:bg-surface-elevated lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </Container>
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <Footer variant="simple" />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        ctaLabel={t('header.cta')}
        ctaHref="/#diagnostico"
      />
    </>
  )
}
